/**
 * Strip dangerous HTML: <script> tags, inline event handlers, and raw JS code from content.
 * Used to clean migrated news content that may contain embedded JavaScript.
 */
export function sanitizeHtml(html: string): string {
  if (!html) return '';

  let cleaned = html;

  // Remove <script>...</script> tags and their contents
  cleaned = cleaned.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

  // Remove inline event handlers (onclick, onerror, onload, etc.)
  cleaned = cleaned.replace(/\s+on\w+\s*=\s*["'][^"']*["']/gi, '');
  cleaned = cleaned.replace(/\s+on\w+\s*=\s*\{[^}]*\}/gi, '');

  // Remove javascript: protocol in hrefs
  cleaned = cleaned.replace(/href\s*=\s*["']javascript:[^"']*["']/gi, 'href="#"');

  // Remove raw document.querySelector / document.getElementById calls that leaked into text
  cleaned = cleaned.replace(/document\.\w+\([^)]*\)(?:\.\w+(?:\([^)]*\))?)*;?/g, '');

  // Remove style tags
  cleaned = cleaned.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '');

  // Trim any resulting whitespace
  cleaned = cleaned.trim();

  return cleaned;
}

/**
 * Convert plain text (with \n line breaks) to safe HTML paragraphs.
 * If the text already contains HTML tags it is returned as-is.
 * Used so that Squarespace-imported posts display with proper line breaks.
 */
export function plainTextToHtml(text: string): string {
  if (!text) return '';
  // Already contains HTML — don't double-convert
  if (/<[a-zA-Z]/.test(text)) return text;
  // Split on double newlines → paragraphs; single newlines → <br/>
  return text
    .split(/\n{2,}/)
    .map(para => `<p>${para.replace(/\n/g, '<br/>')}</p>`)
    .filter(p => p !== '<p></p>')
    .join('\n');
}

/**
 * Turn a standalone Facebook URL (a post/photo/story or a video) that sits on
 * its own line into a Facebook embed — using Meta's PUBLIC plugin iframes, so no
 * Graph API / access token is needed. Only transforms URLs that are alone in a
 * <p> (links inside prose are left as links). Run AFTER plainTextToHtml.
 */
export function embedFacebook(html: string): string {
  if (!html) return html;
  return html.replace(
    /<p>\s*(https?:\/\/(?:www\.|m\.|web\.)?(?:facebook\.com|fb\.watch)\/[^\s"'<]+?)\s*<\/p>/gi,
    (_m, url: string) => {
      const enc = encodeURIComponent(url);
      const isVideo = /\/videos?\/|\/watch|fb\.watch/i.test(url);
      const plugin = isVideo ? "video.php" : "post.php";
      const src = `https://www.facebook.com/plugins/${plugin}?href=${enc}&show_text=true&width=500`;
      // Inline aspect-ratio:auto overrides the content's [&_iframe]:aspect-video.
      return `<div class="fb-embed" style="max-width:552px;margin:1rem auto"><iframe src="${src}" style="width:100%;height:640px;border:none;overflow:hidden;aspect-ratio:auto" scrolling="no" frameborder="0" allowfullscreen="true" allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share" loading="lazy"></iframe></div>`;
    },
  );
}

/**
 * Turn a standalone YouTube URL on its own line into a responsive embedded
 * player (privacy-enhanced nocookie iframe). Mirrors embedFacebook: only URLs
 * alone in a <p> are transformed — links inside prose stay links. Handles
 * watch?v=, youtu.be/ and shorts/ forms, with or without the anchor tag an
 * editor may have wrapped around the pasted URL. Run AFTER plainTextToHtml.
 */
export function embedYouTube(html: string): string {
  if (!html) return html;
  const ID = '([A-Za-z0-9_-]{11})';
  const URL = `https?:\\/\\/(?:www\\.|m\\.)?(?:youtube\\.com\\/(?:watch\\?[^\\s"'<]*v=|shorts\\/)|youtu\\.be\\/)${ID}[^\\s"'<]*`;
  // Bare URL alone in a <p>, or the same URL as the only content of an <a>.
  const re = new RegExp(
    `<p>\\s*(?:<a [^>]*href="[^"]*"[^>]*>\\s*)?(${URL})(?:\\s*<\\/a>)?\\s*<\\/p>`,
    'gi'
  );
  return html.replace(re, (_m, _full, id: string) =>
    `<figure class="yt-embed" style="margin:1rem 0"><iframe src="https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1" title="YouTube video" allowfullscreen loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" style="width:100%;aspect-ratio:16/9;border:0;border-radius:8px"></iframe></figure>`
  );
}

/**
 * Upgrade the scraper's trailing "Read the full story on …" text link into a
 * visible outline button (class bb-source-btn, styled in globals.css with the
 * app-wide hover-invert). Runs at render time so OLD articles get the button
 * too, not just freshly scraped ones.
 */
export function styleSourceLinks(html: string): string {
  if (!html) return html;
  return html.replace(
    /<a\s+(?![^>]*bb-source-btn)([^>]*href="https?:\/\/[^"]*"[^>]*)>(\s*Read the full story[^<]*)<\/a>/gi,
    '<a class="bb-source-btn" $1>$2</a>'
  );
}

/**
 * Strip all HTML tags, returning only text content.
 * Useful for generating clean excerpts from HTML content.
 */
export function stripHtml(html: string): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

/**
 * Clean an excerpt specifically - strip JS, HTML, and truncate.
 */
export function cleanExcerpt(text: string, maxLength: number = 200): string {
  if (!text) return '';
  // First sanitize any HTML/JS
  let clean = sanitizeHtml(text);
  // Strip remaining HTML tags for plain text excerpt
  clean = stripHtml(clean);
  // Truncate
  if (clean.length > maxLength) {
    clean = clean.substring(0, maxLength).replace(/\s+\S*$/, '') + '...';
  }
  return clean;
}
